import { useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDashboardStore } from '@store'
import {
	StatCard,
	ApprovalQueue,
	ProjectCard,
	TaskList,
	QuickActions
} from '@components/features/dashboard/widgets'
import {
	BarChart,
	DonutChart,
	ProgressRing
} from '@components/features/dashboard/charts'
import {
	DashboardGrid,
	WidgetContainer,
	StatsRow
} from '@components/features/dashboard/layouts'
import './Dashboard.scss'

/**
 * Project Manager Dashboard
 *
 * Displays project-focused metrics, pending approvals,
 * team workload distribution, and project health overview.
 */
const ProjectManagerDashboard = () => {
	const navigate = useNavigate()

	// Dashboard store
	const {
		pmOverview,
		pmProjects,
		resourceAllocation,
		myApprovals,
		loading,
		error,
		autoRefreshEnabled,
		fetchProjectManagerDashboard,
		startAutoRefresh,
		stopAutoRefresh,
		getTimeSinceUpdate
	} = useDashboardStore()

	// Fetch dashboard data on mount
	useEffect(() => {
		fetchProjectManagerDashboard()

		// Start auto-refresh every 60 seconds
		const cleanup = startAutoRefresh('project-manager', 60000)

		return () => {
			cleanup()
			stopAutoRefresh()
		}
	}, [fetchProjectManagerDashboard, startAutoRefresh, stopAutoRefresh])

	// Manual refresh handler
	const handleRefresh = useCallback(() => {
		fetchProjectManagerDashboard()
	}, [fetchProjectManagerDashboard])

	// Quick action handlers
	const quickActions = [
		{
			icon: '📋',
			label: 'Review Approvals',
			onClick: () => navigate('/approvals'),
			permission: null
		},
		{
			icon: '📁',
			label: 'My Projects',
			onClick: () => navigate('/projects'),
			permission: null
		},
		{
			icon: '👥',
			label: 'Team Tasks',
			onClick: () => navigate('/tasks/team'),
			permission: null
		},
		{
			icon: '📊',
			label: 'Project Reports',
			onClick: () => navigate('/reports/projects'),
			permission: 'canViewReports'
		}
	]

	// Format projects for ProjectCard
	const projectCards = (pmProjects || []).slice(0, 4).map(project => ({
		id: project.id,
		name: project.name,
		progress: project.progress || 0,
		status: project.status,
		dueDate: project.due_date,
		teamSize: project.team_size || 0,
		tasksCompleted: project.completed_tasks || 0,
		totalTasks: project.total_tasks || 0
	}))

	// Format pending approvals for ApprovalQueue
	const approvalItems = (myApprovals || []).map(item => ({
		id: item.id,
		title: item.title || item.name,
		type: item.type || 'checklist',
		submittedBy: item.submitted_by_name || item.submitted_by,
		submittedAt: item.submitted_at || item.created_at,
		priority: item.priority || 'medium'
	}))

	// Format resource allocation for BarChart
	const resourceChartData = (resourceAllocation || []).map(member => ({
		name: member.name || `${member.first_name} ${member.last_name}`,
		assigned: member.assigned_tasks || 0,
		completed: member.completed_tasks || 0,
		overdue: member.overdue_tasks || 0
	}))

	// Project status distribution for DonutChart
	const projectStatusData = pmOverview?.project_status_distribution || [
		{ name: 'On Track', value: pmOverview?.projects_on_track || 0, color: 'var(--color-success)' },
		{ name: 'At Risk', value: pmOverview?.projects_at_risk || 0, color: 'var(--color-warning)' },
		{ name: 'Delayed', value: pmOverview?.projects_delayed || 0, color: 'var(--color-danger)' }
	]

	// Overall project health score
	const overallHealth = pmOverview?.overall_health_score || 0

	return (
		<div className="dashboard-page project-manager-dashboard">
			{/* Page Header */}
			<div className="page-header">
				<div className="page-header__content">
					<div>
						<h1 className="page-title">Project Manager Dashboard</h1>
						<nav className="breadcrumb">
							<span className="breadcrumb__item breadcrumb__item--active">Dashboard</span>
						</nav>
					</div>
					<div className="page-header__actions">
						<span className="last-updated">
							Last updated: {getTimeSinceUpdate()}
							{autoRefreshEnabled && <span className="auto-refresh-badge">Auto</span>}
						</span>
						<button
							className="btn btn--secondary btn--sm"
							onClick={handleRefresh}
							disabled={loading}
						>
							{loading ? 'Refreshing...' : 'Refresh'}
						</button>
					</div>
				</div>
			</div>

			{/* Error Display */}
			{error && (
				<div className="alert alert--danger">
					<p>{error}</p>
					<button onClick={handleRefresh}>Retry</button>
				</div>
			)}

			{/* Stats Row */}
			<StatsRow>
				<StatCard
					title="Assigned Projects"
					value={pmOverview?.assigned_projects ?? '-'}
					subtitle="Under your management"
					icon="📁"
					variant="primary"
					loading={loading}
					onClick={() => navigate('/projects')}
				/>
				<StatCard
					title="Pending Approvals"
					value={pmOverview?.pending_approvals ?? '-'}
					subtitle="Awaiting your review"
					icon="📋"
					variant="warning"
					loading={loading}
					onClick={() => navigate('/approvals')}
				/>
				<StatCard
					title="Team Tasks"
					value={pmOverview?.team_tasks_in_progress ?? '-'}
					subtitle="Currently in progress"
					icon="⏳"
					variant="info"
					loading={loading}
					onClick={() => navigate('/tasks/team')}
				/>
				<StatCard
					title="Overdue Items"
					value={pmOverview?.overdue_items ?? '-'}
					subtitle="Require attention"
					icon="⚠️"
					variant="danger"
					loading={loading}
					onClick={() => navigate('/tasks?status=overdue')}
				/>
			</StatsRow>

			{/* Pending Approvals and Project Health Row */}
			<DashboardGrid columns={2}>
				{/* Pending Approvals */}
				<WidgetContainer
					title="Pending Approvals"
					onAction={() => navigate('/approvals')}
					actionLabel="View All"
					loading={loading}
				>
					<ApprovalQueue
						items={approvalItems}
						onApprove={(id) => navigate(`/approvals/${id}?action=approve`)}
						onReject={(id) => navigate(`/approvals/${id}?action=reject`)}
						onView={(id) => navigate(`/approvals/${id}`)}
						loading={loading}
						emptyMessage="No pending approvals"
					/>
				</WidgetContainer>

				{/* Project Health Overview */}
				<WidgetContainer
					title="Project Health Overview"
					loading={loading}
				>
					<div className="project-health-container">
						<div className="project-health__gauge">
							<ProgressRing
								value={overallHealth}
								max={100}
								size={140}
								strokeWidth={12}
								showPercentage={true}
								label="Overall Health"
								loading={loading}
							/>
						</div>
						<div className="project-health__chart">
							<DonutChart
								data={projectStatusData}
								height={180}
								innerRadius={40}
								outerRadius={70}
								showLegend={true}
								loading={loading}
							/>
						</div>
					</div>
				</WidgetContainer>
			</DashboardGrid>

			{/* Projects and Team Workload Row */}
			<DashboardGrid columns={2}>
				{/* My Projects */}
				<WidgetContainer
					title="My Projects"
					onAction={() => navigate('/projects')}
					actionLabel="View All"
					loading={loading}
				>
					<div className="projects-list">
						{projectCards.length === 0 ? (
							<div className="empty-state">
								<p>No projects assigned</p>
							</div>
						) : (
							projectCards.map(project => (
								<ProjectCard
									key={project.id}
									project={project}
									onClick={() => navigate(`/projects/${project.id}`)}
									loading={loading}
									compact={true}
								/>
							))
						)}
					</div>
				</WidgetContainer>

				{/* Team Workload Distribution */}
				<WidgetContainer
					title="Team Workload"
					onAction={() => navigate('/team')}
					actionLabel="View Team"
					loading={loading}
				>
					<BarChart
						data={resourceChartData}
						bars={[
							{ dataKey: 'assigned', name: 'Assigned', color: 'var(--color-primary)' },
							{ dataKey: 'completed', name: 'Completed', color: 'var(--color-success)' },
							{ dataKey: 'overdue', name: 'Overdue', color: 'var(--color-danger)' }
						]}
						xAxisKey="name"
						height={250}
						showGrid={true}
						showLegend={true}
						loading={loading}
					/>
				</WidgetContainer>
			</DashboardGrid>

			{/* Quick Actions */}
			<QuickActions actions={quickActions} />
		</div>
	)
}

export default ProjectManagerDashboard
